import classnames from 'classnames'
import React, { Children, cloneElement, ReactNode, useEffect, useRef, useState } from 'react'
import { SwiperRef } from 'swiper/react'

import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'

// style

import Swiper from '../swiper'
import { DOTS_POSITION, NAV_POSITION } from '../swiper/Swiper'

import stylesTabs from './tabs.module.scss'
import styles from './tabsCarousel.module.scss'

export interface SlideProps {
  children: ReactNode
  id: string
  title: string
  active?: boolean
  showSlideTitle?: boolean
  className?: string
  tracker?: string
}

export const Slide = ({ title, children, showSlideTitle = true, className }: SlideProps) => {
  return (
    <div className={classnames(styles.slide_panel, className)}>
      {showSlideTitle && <h3 className={styles.slide_panel__title}>{title}</h3>}
      {children}
    </div>
  )
}

export interface TabsCarouselProps {
  children?: React.ReactElement<SlideProps> | React.ReactElement<SlideProps>[]
  className?: string
  tracker?: string
}

const TabsCarousel = ({ children, className = '', tracker }: TabsCarouselProps) => {
  const [activeSlide, setActiveSlide] = useState(0)
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const classes = classnames(stylesTabs.tabs, styles.tabsCarouselWrap, className)

  const carouselRef = useRef<SwiperRef | null>(null)
  const onSlideChange = (newSlide: number) => {
    setActiveSlide(newSlide)
    if (tracker) {
      trackClickEvent(
        TELEMETRY_EVENT_NAMES.BUTTON_CLICK,
        tracker
          ? {
              trackGA: true,
              trackInternal: true,
              // @i18n-ignore
              category: 'navigation',
              action: 'click__tabs_carousel__tab',
              label: newSlide,
              value: tracker,
            }
          : undefined,
      )
    }
  }
  useEffect(() => {
    if (carouselRef.current?.swiper) {
      carouselRef.current.swiper.slideTo(activeSlide)
    }
  }, [carouselRef, activeSlide])
  return (
    <div className={classes}>
      {isDesktop && (
        <div className={classnames(styles.tabsCarouselContainer, styles.slide_nav__warp, 'tabButtonWrap')}>
          {Children.map(children, (tab, index) => {
            if (!tab) return null
            return (
              <button
                key={tab.props.id}
                onClick={() => {
                  onSlideChange(index)
                }}
                className={`${classnames(styles.slide_nav__button, {
                  [styles.slide_nav__button__active]: index === activeSlide,
                })} `}
              >
                {tab.props.title}
              </button>
            )
          })}
        </div>
      )}
      <Swiper
        ref={carouselRef}
        variableWidth
        showDots={!isDesktop}
        dotsPosition={DOTS_POSITION.BOTTOM}
        showNavigator={isDesktop}
        navPosition={NAV_POSITION.CENTER}
        numberOfVisibleSlides={1}
        afterChange={swiperInstance => {
          setActiveSlide(swiperInstance.activeIndex)
        }}
        className={styles.carouselWraper}
        freeMode={!isDesktop}
        /** TODO: pass aria label - validate on about us page and reviews */
      >
        {Children.map(children, slide => {
          if (!slide) return null

          return cloneElement(slide, {
            ...slide.props,
            showSlideTitle: !isDesktop,
            className: classnames(styles.slideWrap, styles.tabsCarouselContainer),
          })
        })}
      </Swiper>
    </div>
  )
}

export default TabsCarousel
